/**
 * 隐私合规同意管理（《个人信息保护法》第13、14条：处理个人信息应取得个人同意）
 *
 * - 用户同意前：不加载任何统计/监控 SDK，不上报任何埋点（见 @/utils/web-Tracking）
 * - 用户同意后：动态加载百度统计、阿里云 ARMS；SLS 埋点由 web-Tracking 延迟实例化
 * - 登录页勾选协议并登录成功视为同意（login.vue 中调用 grantPrivacyConsent）
 */
import { computed, ref } from 'vue'

/** 已同意隐私政策的标记 */
export const CONSENT_KEY = 'yt_privacy_consent'
/** 拒绝授权的时间戳（72小时冷却期内不再弹窗） */
export const REFUSE_KEY = 'yt_privacy_consent_refuse'
const REFUSE_COOLDOWN = 72 * 60 * 60 * 1000

/** localStorage 不具响应性，授权状态每次变更时自增此信号，供 computed 依赖 */
const consentSignal = ref(0)
const bumpConsentSignal = () => consentSignal.value++

/** 是否已同意隐私政策（统计SDK仅在同意后初始化）；响应式版本见 privacyConsent */
export const hasPrivacyConsent = () => {
  consentSignal.value
  return window.localStorage.getItem(CONSENT_KEY) === '1'
}

/** 同意状态的响应式只读视图，供组件在授权变更后自动刷新 */
export const privacyConsent = computed(hasPrivacyConsent)

/** 用户在隐私指引中点击「暂不同意」的信号，自增供 watch 触发（登录态下需引导走撤回授权流程） */
export const consentRefuseSignal = ref(0)

/** 是否需要弹出隐私指引：未同意且不在拒绝冷却期内 */
export const shouldShowConsentDialog = () => {
  if (hasPrivacyConsent()) return false
  const refuseTime = Number(window.localStorage.getItem(REFUSE_KEY) || 0)
  return Date.now() - refuseTime > REFUSE_COOLDOWN
}

/** 记录用户拒绝（72小时内不再询问，过期后重新询问） */
export const refusePrivacyConsent = () => {
  window.localStorage.setItem(REFUSE_KEY, Date.now().toString())
  bumpConsentSignal()
}

/** 撤回隐私授权：清除同意状态并记录拒绝冷却（个人中心「撤回授权」入口使用，《个保法》第15条） */
export const revokePrivacyConsent = () => {
  window.localStorage.removeItem(CONSENT_KEY)
  refusePrivacyConsent()
}

const loadScript = (src: string) => {
  const script = document.createElement('script')
  script.src = src
  const first = document.getElementsByTagName('script')[0]
  first.parentNode?.insertBefore(script, first)
}

/** 动态加载第三方统计 SDK（仅在用户同意后调用，幂等） */
export const initAnalytics = () => {
  const w = window as any
  // 百度统计
  if (!w._hmt) {
    w._hmt = []
    loadScript('https://hm.baidu.com/hm.js?606d9a347cb2ba1a0c36d1816c6cd01b')
  }
  // 阿里云 ARMS 用户体验监控（沿用原 index.html 的域名判断：仅 ytaxx 站点上报）
  if (!w.__rum && window.location.origin.indexOf('ytaxx') !== -1) {
    w.__rum = {
      pid: 'ci3i146suv@0d7fbb1f667feaa',
      endpoint: 'https://ci3i146suv-default-cn.rum.aliyuncs.com',
      env: 'prod',
      spaMode: 'history'
    }
    loadScript('https://sdk.rum.aliyuncs.com/v2/browser-sdk.js')
  }
}

/** 记录用户同意并立即启用统计 SDK */
export const grantPrivacyConsent = () => {
  window.localStorage.setItem(CONSENT_KEY, '1')
  window.localStorage.removeItem(REFUSE_KEY) // 同意后清除拒绝冷却标记
  bumpConsentSignal()
  initAnalytics()
}

/** 应用启动时调用：已同意过的用户恢复统计 SDK */
export const setupAnalytics = () => {
  if (hasPrivacyConsent()) initAnalytics()
}
